class CartDuplicateHandler {
  constructor() {
    this.cartItems = [];
    this.translations = window.cartTranslations || {
      alreadyInCart: 'Already in cart',
      addToCart: 'Add to cart',
      quickView: 'Quick view'
    };
    this.init();
  }

  async init() {
    await this.fetchCart();
    this.updateAllButtons();
    
    // Listen to various cart events
    document.addEventListener('cart:refresh', () => this.handleCartUpdate());
    document.addEventListener('cart:updated', () => this.handleCartUpdate());
    
    // Listen to native Shopify events
    this.interceptCartAdd();
    
    // Observe DOM changes (for dynamically added products)
    this.observeDOM();
  }

  async fetchCart() {
    // Coalesce overlapping callers into a single request: cart:refresh and
    // cart:updated are frequently dispatched together for the same single
    // mutation, and the intercepted-fetch path (below) can react to the same
    // mutation too - without this, each would fire its own identical
    // /cart.js GET. While one fetch is in flight, later callers share the
    // same promise instead of starting a new request.
    if (this._pendingFetch) return this._pendingFetch;

    this._pendingFetch = (async () => {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();
        this.cartItems = cart.items.map(item => item.product_id);
        return cart;
      } catch (error) {
        console.error('Error fetching cart:', error);
        return { items: [] };
      } finally {
        this._pendingFetch = null;
      }
    })();

    return this._pendingFetch;
  }

  isProductInCart(productId) {
    return this.cartItems.includes(parseInt(productId));
  }

  async handleCartUpdate() {
    await this.fetchCart();
    this.updateAllButtons();
  }

  // Intercept all requests to /cart/add.js
  interceptCartAdd() {
    const originalFetch = window.fetch;
    
    window.fetch = async (...args) => {
      const response = await originalFetch(...args);
      
      // Check if this is a cart request
      const url = args[0];
      if (typeof url === 'string' && (url.includes('/cart/add') || url.includes('/cart/change') || url.includes('/cart/update'))) {
        // Wait a moment and refresh state
        setTimeout(() => this.handleCartUpdate(), 100);
      }
      
      return response;
    };

    // Handle XMLHttpRequest (older implementations)
    const originalOpen = XMLHttpRequest.prototype.open;
    const originalSend = XMLHttpRequest.prototype.send;
    const self = this;

    XMLHttpRequest.prototype.open = function(method, url) {
      this._url = url;
      return originalOpen.apply(this, arguments);
    };

    XMLHttpRequest.prototype.send = function() {
      this.addEventListener('load', function() {
        if (this._url && (this._url.includes('/cart/add') || this._url.includes('/cart/change') || this._url.includes('/cart/update'))) {
          setTimeout(() => self.handleCartUpdate(), 100);
        }
      });
      return originalSend.apply(this, arguments);
    };
  }

  // Observe DOM changes (for infinite scroll, quick view, etc.)
  observeDOM() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length) {
          // Check if new products were added
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1 && (node.classList?.contains('product-item') || node.querySelector?.('.product-item'))) {
              this.updateAllButtons();
            }
          });
        }
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  updateAllButtons() {
    // Find all product cards
    const productCards = document.querySelectorAll('.product-item[data-product-id]');
    
    productCards.forEach(card => {
      const productId = card.dataset.productId;
      const isInCart = this.isProductInCart(productId);
      
      // Find ONLY buttons in this specific product card
      const addButtons = card.querySelectorAll('button[name="add"], .btn__quick-shop');
      
      addButtons.forEach(button => {
        // Check if button belongs to this product
        const buttonProductId = this.getButtonProductId(button, card);
        if (buttonProductId && parseInt(buttonProductId) === parseInt(productId)) {
          this.updateButton(button, isInCart, productId);
        }
      });
    });

    // Also handle buttons on product page (PDP)
    this.updateProductPageButtons();
  }

  // Helper function to find product ID for button
  getButtonProductId(button, card) {
    // Method 1: From product card (most reliable)
    if (card && card.dataset.productId) {
      return card.dataset.productId;
    }

    // Method 2: From form
    const form = button.closest('form[action*="/cart/add"]');
    if (form) {
      const variantInput = form.querySelector('[name="id"]');
      if (variantInput && variantInput.dataset.productId) {
        return variantInput.dataset.productId;
      }
    }

    // Method 3: From closest product-item
    const productItem = button.closest('.product-item[data-product-id]');
    if (productItem) {
      return productItem.dataset.productId;
    }

    return null;
  }

  updateProductPageButtons() {
    // Find product ID from JSON-LD or data attribute
    let productId = null;
    
    // Method 1: Look in product section
    const productSection = document.querySelector('[data-product-id]');
    if (productSection && !productSection.classList.contains('product-item')) {
      productId = parseInt(productSection.dataset.productId);
    }
    
    // Method 2: Look in JSON
    if (!productId) {
      const productJsonElement = document.querySelector('.productJson');
      if (productJsonElement) {
        try {
          const productData = JSON.parse(productJsonElement.textContent);
          productId = productData.id;
        } catch (e) {
          console.error('Error parsing product JSON:', e);
        }
      }
    }

    if (!productId) return;

    const isInCart = this.isProductInCart(productId);

    // Find all add to cart forms on PDP
    const productForms = document.querySelectorAll('form[action*="/cart/add"]');
    
    productForms.forEach(form => {
      // Check if form is NOT in a product card
      if (form.closest('.product-item')) return;
      
      const addButton = form.querySelector('button[name="add"], button[type="submit"]');
      if (addButton) {
        this.updateButton(addButton, isInCart, productId);
      }
    });

    // Also handle sticky add to cart
    const stickyAddCart = document.querySelector('sticky-add-cart');
    if (stickyAddCart) {
      const stickyButton = stickyAddCart.querySelector('button[name="add"], button[type="submit"]');
      if (stickyButton) {
        this.updateButton(stickyButton, isInCart, productId);
      }
    }
  }

  updateButton(button, isInCart, productId) {
    // Find text in button - various possible selectors
    const buttonText = button.querySelector('.product-addtocart-btn, .product-quickview-btn, .btn-text, .btn__text');
    
    // Save original text if not already saved
    if (!button.dataset.originalText) {
      if (buttonText) {
        button.dataset.originalText = buttonText.textContent.trim();
      } else {
        button.dataset.originalText = button.textContent.trim();
      }
    }

    if (isInCart) {
      // Product is in cart - disable button
      button.disabled = true;
      button.classList.add('btn-in-cart');
      button.setAttribute('aria-label', this.translations.alreadyInCart);
      button.dataset.productInCart = 'true';
      
      if (buttonText) {
        buttonText.textContent = this.translations.alreadyInCart;
      } else {
        // Preserve icon if it exists
        const icon = button.querySelector('svg, .icon');
        if (icon) {
          const iconClone = icon.cloneNode(true);
          button.innerHTML = '';
          button.appendChild(iconClone);
          button.appendChild(document.createTextNode(' ' + this.translations.alreadyInCart));
        } else {
          button.textContent = this.translations.alreadyInCart;
        }
      }
    } else {
      // Product is not in cart - enable button
      button.disabled = false;
      button.classList.remove('btn-in-cart');
      button.removeAttribute('aria-label');
      button.dataset.productInCart = 'false';
      
      // Restore original text
      const originalText = button.dataset.originalText;
      if (originalText) {
        if (buttonText) {
          buttonText.textContent = originalText;
        } else {
          // Preserve icon if it exists
          const icon = button.querySelector('svg, .icon');
          if (icon) {
            const iconClone = icon.cloneNode(true);
            button.innerHTML = '';
            button.appendChild(iconClone);
            button.appendChild(document.createTextNode(' ' + originalText));
          } else {
            button.textContent = originalText;
          }
        }
      }
    }
  }
}

// Initialization
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.cartDuplicateHandler = new CartDuplicateHandler();
  });
} else {
  window.cartDuplicateHandler = new CartDuplicateHandler();
}

// Export for other scripts
window.refreshCartButtons = () => {
  if (window.cartDuplicateHandler) {
    window.cartDuplicateHandler.handleCartUpdate();
  }
};